import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Clock,
  Check,
  CheckCircle,
  Loader,
  BookOpen
} from 'lucide-react';
import emailjs from '@emailjs/browser';

// Course data structure - in a real app, this would come from an API
const allCourses = [
  {
    id: 1,
    title: 'Tech Explorers: LEGO Robotics & Coding (6–10 г.)',
    slug: 'tech-explorers-lego-robotics-coding',
    description: 'Въвеждане в света на роботиката и програмирането чрез LEGO, с фокус върху логическо мислене и STEM умения.',
    seoTitle: 'LEGO роботика за деца 6-10 г. | Akava',
    seoDescription: 'LEGO роботика за деца 6-10 години. Курс по роботика за деца с LEGO програмиране и STEM игри. Записвания отворени!',
    seoH1: 'LEGO роботика за деца (6–10 г.) – Роботика и програмиране',
    fullDescription: 'LEGO роботика за деца на възраст 6-10 години е идеален старт в света на технологиите. Нашият курс по роботика за деца комбинира забавлението на LEGO с основите на програмирането, като развива логическо мислене и STEM умения. Чрез LEGO програмиране децата научават основните принципи на роботиката, работа със сензори и двигатели, както и блоково програмиране. Този STEM курс за деца е специално разработен за младите изследователи и не изисква предишен опит. Децата ще конструират собствени роботи, ще ги програмират да изпълняват различни задачи и ще усвояват основните концепции на роботиката и програмирането по интерактивен и забавен начин.',
    duration: '8 седмици / 8 занятия',
    price: 185,
    formatted_price: '185 €',
    features: [
      'Основи на роботиката',
      'Сензори и двигатели',
      'Блоково програмиране',
      'STEM игри',
      'Сертификат за завършване'
    ],
    learningSections: [
      {
        title: 'Основи на роботиката',
        items: [
          'Запознаване с LEGO роботика и нейните възможности',
          'Конструиране на първи роботи с LEGO елементи',
          'Разбиране на основните компоненти: сензори и двигатели',
          'Първи стъпки в света на роботиката чрез практични упражнения'
        ]
      },
      {
        title: 'Работа със сензори и двигатели',
        items: [
          'Използване на различни типове сензори',
          'Контрол на двигатели и движения на роботите',
          'Синхронизация на сензори и двигатели за по-сложни задачи',
          'Създаване на интерактивни роботи, които реагират на околната среда'
        ]
      },
      {
        title: 'Блоково програмиране',
        items: [
          'Запознаване с LEGO програмиране чрез визуални блокове',
          'Създаване на програми за управление на роботи',
          'Работа с цикли, условия и логически оператори',
          'Развитие на алгоритмично мислене чрез практични проекти'
        ]
      },
      {
        title: 'STEM игри и проекти',
        items: [
          'Участие в STEM игри и предизвикателства',
          'Създаване на собствени роботични проекти',
          'Работа в екип за решаване на комплексни задачи',
          'Развитие на креативност и аналитично мислене чрез роботика'
        ]
      },
      {
        title: 'Сертификат за завършване',
        items: [
          'Получаване на сертификат за завършване на курса',
          'Демонстрация на придобити умения и знания',
          'Портфолио от създадени роботични проекти',
          'Подготовка за следващи нива в роботиката и програмирането'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 2,
    title: 'Scratch Creators (10–12 г.)',
    slug: 'scratch-creators',
    description: 'Създаване на анимации, игри и интерактивни истории чрез визуално програмиране.',
    seoTitle: 'Scratch курс за деца 10-12 г. | Akava Academy',
    seoDescription: 'Scratch курс за деца 10-12 години. Научете визуално програмиране, създаване на игри и анимации. Записвания отворени!',
    seoH1: 'Scratch Creators (10–12 г.)',
    fullDescription: 'Scratch е идеален старт в програмирането за деца на възраст 10-12 години. Чрез блоково програмиране децата научават основните принципи на програмирането, развиват логическо мислене и творчески способности. Курсът по Scratch програмиране е специално разработен за начинаещи и не изисква предишен опит. Децата ще създават собствени игри, анимации и интерактивни истории, като усвояват основните концепции на програмирането по забавен и интерактивен начин.',
    duration: '8 седмици / 8 занятия',
    price: 164,
    formatted_price: '164 €',
    features: [
      'Създаване на анимации',
      'Разработване на игри',
      'Интерактивни истории',
      'Основи на логическо мислене',
      'Творчески проекти'
    ],
    learningSections: [
      {
        title: 'Основи на визуалното програмиране',
        items: [
          'Запознаване с платформата Scratch и нейните възможности',
          'Работа с блокове и команди за програмиране',
          'Разбиране на основните концепции: променливи, цикли, условия',
          'Първи стъпки в програмирането чрез интерактивни упражнения'
        ]
      },
      {
        title: 'Създаване на игри със Scratch',
        items: [
          'Разработване на собствени игри от нулата',
          'Работа със спрайтове, движения и взаимодействия',
          'Добавяне на звуци и ефекти към игрите',
          'Създаване на игри с множество нива и предизвикателства'
        ]
      },
      {
        title: 'Анимации със Scratch',
        items: [
          'Създаване на анимационни истории и сюжети',
          'Работа с анимационни ефекти и преходи',
          'Синхронизация на движения и звуци',
          'Разработване на интерактивни анимационни проекти'
        ]
      },
      {
        title: 'Развитие на логическо мислене',
        items: [
          'Усвояване на алгоритмично мислене',
          'Разрешаване на проблеми чрез програмиране',
          'Развитие на креативност и аналитично мислене',
          'Прилагане на логически структури в реални проекти'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 3,
    title: 'Python Start Lab (12–14 г.)',
    slug: 'python-start-lab',
    description: 'Първи стъпки в програмирането с Python чрез забавни проекти и практически приложения.',
    seoTitle: 'Python курс за деца 12-14 г. | Akava',
    seoDescription: 'Python курс за деца 12-14 години. Научете програмиране с Python чрез мини игри и практически проекти. Записвания отворени!',
    seoH1: 'Python курс за деца (12–14 г.) – Програмиране с Python',
    fullDescription: 'Python курс за деца на възраст 12-14 години е идеален старт в програмирането за начинаещи. Курсът комбинира теория с практика. Темите включват Python синтаксис, базови алгоритми и логическо мислене, създаване на мини игри с Python и чатботове. Програмата на курса е специално разработена за ученици и не изисква предишен опит в програмирането.',
    duration: '8 седмици / 8 занятия',
    price: 195,
    formatted_price: '195 €',
    features: [
      'Програмиране на мини игри',
      'Създаване на чатботове',
      'Базови алгоритми',
      'Python синтаксис',
      'Практически проекти'
    ],
    learningSections: [
      {
        title: 'Основи на Python програмирането',
        items: [
          'Запознаване с Python езика и неговите възможности',
          'Усвояване на Python синтаксис и основни команди',
          'Работа с променливи, типове данни и оператори',
          'Първи стъпки в програмирането с Python чрез практични упражнения'
        ]
      },
      {
        title: 'Базови алгоритми и логическо мислене',
        items: [
          'Разбиране на основните алгоритмични структури',
          'Работа с цикли (for, while) и условни оператори (if, else)',
          'Създаване на функции и модули в Python',
          'Развитие на логическо мислене чрез решаване на задачи'
        ]
      },
      {
        title: 'Програмиране на мини игри с Python',
        items: [
          'Създаване на първи игри с Python от нулата',
          'Работа с графични библиотеки за игри',
          'Имплементиране на игрова логика и взаимодействия',
          'Разработване на забавни и интерактивни мини игри'
        ]
      },
      {
        title: 'Създаване на чатботове',
        items: [
          'Разбиране на основите на чатботовете',
          'Програмиране на интерактивни разговори',
          'Работа с входни и изходни данни',
          'Създаване на функционални чатботове с Python'
        ]
      },
      {
        title: 'Практически проекти',
        items: [
          'Разработване на собствени програмни проекти',
          'Прилагане на усвоените знания в реални сценарии',
          'Работа в екип за създаване на по-сложни приложения',
          'Изграждане на портфолио от Python проекти'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 5,
    title: 'UX Discovery (16–18 г.)',
    slug: 'ux-discovery',
    description: 'Разглеждане на основите на потребителското изживяване и дизайн на интерфейси чрез практически упражнения.',
    seoTitle: 'UX UI курс за тийнейджъри 16-18 г. | Akava',
    seoDescription: 'UX UI курс за тийнейджъри 16-18 г. Научете UX UI дизайн, wireframes и прототипиране. Практически курс по UX дизайн. Записвания отворени!',
    seoH1: 'UX UI курс (16–18 г.) – UX UI дизайн за начинаещи',
    fullDescription: 'UX UI курс за тийнейджъри на възраст 16-18 години обхваща основните принципи на потребителското изживяване и дизайн на интерфейси. Програмата комбинира теорията с практиката, като учениците работят с wireframes, прототипиране и тестване на дизайн. Курсът предоставя необходимите знания за създаване на интуитивни и ефективни дизайни.',
    duration: '8 седмици / 8 занятия',
    price: 185,
    formatted_price: '185 €',
    features: [
      'Основи на UX/UI',
      'Wireframes',
      'Потребителско изживяване',
      'Прототипиране',
      'Тестване на дизайн'
    ],
    learningSections: [
      {
        title: 'Основи на UX UI дизайн',
        items: [
          'Запознаване с UX UI дизайн и неговите принципи',
          'Разбиране на разликата между UX и UI дизайн',
          'Основни концепции на потребителското изживяване',
          'Дизайн мислене и подход към решаване на проблеми'
        ]
      },
      {
        title: 'Wireframes и структуриране',
        items: [
          'Създаване на wireframes за различни типове интерфейси',
          'Структуриране на информация и навигация',
          'Работа с layout и визуална йерархия',
          'Планиране на потребителски пътеки и взаимодействия'
        ]
      },
      {
        title: 'Прототипиране и интерактивност',
        items: [
          'Създаване на интерактивни прототипи',
          'Работа с инструменти за прототипиране',
          'Демонстриране на функционалност и взаимодействия',
          'Итеративно подобряване на дизайна'
        ]
      },
      {
        title: 'Дизайн на интерфейси',
        items: [
          'Принципи на визуалния дизайн в UI',
          'Работа с цветове, типография и иконография',
          'Адаптивен дизайн за различни устройства'
        ]
      },
      {
        title: 'Тестване на дизайн и обратна връзка',
        items: [
          'Методи за тестване на потребителско изживяване',
          'Събиране и анализ на обратна връзка',
          'Итеративно подобряване на дизайна',
          'Прилагане на резултатите от тестването'
        ]
      },
      {
        title: 'Практически проекти и портфолио',
        items: [
          'Разработване на собствени UX UI проекти',
          'Прилагане на усвоените знания в реални сценарии',
          'Създаване на портфолио от дизайн проекти',
          'Подготовка за следващи стъпки в дизайн кариерата'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 6,
    title: 'CyberSmart Teens (15–18 г.)',
    slug: 'cybersmart-teens',
    description: 'Обучение за безопасност в дигиталния свят и защита на лични данни.',
    duration: '8 седмици / 8 занятия',
    price: 174,
    formatted_price: '174 €',
    features: [
      'Онлайн безопасност',
      'Дигитален отпечатък',
      'AI етика',
      'Защита на данни',
      'Киберсигурност основи'
    ]
  },
  {
    id: 7,
    title: 'AI for Teens (15–18 г.)',
    slug: 'ai-for-teens',
    description: 'Разбиране на изкуствения интелект, неговите приложения и възможности чрез практически примери и проекти.',
    seoTitle: 'AI изкуствен интелект курс 15-18 г. | Akava',
    seoDescription: 'AI изкуствен интелект курс за тийнейджъри 15-18 г. Научете ChatGPT, машинно обучение и практически AI проекти. Записвания отворени!',
    seoH1: 'AI изкуствен интелект курс (15–18 г.) – Практическо обучение',
    fullDescription: 'AI изкуствен интелект за тийнейджъри на възраст 15-18 години е идеален избор за тези, които искат да разберат и овладеят технологиите на бъдещето. Курсът обхваща основните концепции на AI, неговите приложения и практическите възможности. Учениците научават как работи AI, как се използват ChatGPT и текстови модели, както и основите на машинното обучение.',
    duration: '10 седмици / 10 занятия',
    price: 246,
    formatted_price: '246 €',
    features: [
      'Как работи AI',
      'ChatGPT и текстови модели',
      'Машинно обучение',
      'Визуални модели',
      'Практически AI проекти'
    ],
    learningSections: [
      {
        title: 'Как работи AI – Основни концепции',
        items: [
          'Запознаване с изкуствения интелект и неговите възможности',
          'Разбиране на основните принципи и механизми на AI',
          'История и развитие на изкуствения интелект',
          'Приложения на AI в различни сфери и индустрии'
        ]
      },
      {
        title: 'ChatGPT и текстови модели',
        items: [
          'Работа с ChatGPT и други текстови AI модели',
          'Разбиране как функционират езиковите модели',
          'Практическо използване на ChatGPT за различни задачи',
          'Създаване на собствени приложения с текстови модели'
        ]
      },
      {
        title: 'Машинно обучение – Основи',
        items: [
          'Въведение в машинното обучение и неговите типове',
          'Работа с данни и обучение на модели',
          'Разбиране на алгоритмите за машинно обучение',
          'Практически проекти с машинно обучение'
        ]
      },
      {
        title: 'Визуални модели и обработка на изображения',
        items: [
          'Работа с AI модели за обработка на изображения',
          'Генериране и анализ на визуално съдържание',
          'Приложения на визуалните AI модели',
          'Създаване на проекти с визуални модели'
        ]
      },
      {
        title: 'Практически AI проекти',
        items: [
          'Разработване на собствени AI приложения',
          'Прилагане на усвоените знания в реални сценарии',
          'Работа в екип за създаване на комплексни AI проекти',
          'Изграждане на портфолио от практически AI проекти'
        ]
      },
      {
        title: 'Бъдещи професии и възможности',
        items: [
          'Разбиране на ролята на AI в бъдещите професии',
          'Възможности за развитие в сферата на изкуствения интелект',
          'Подготовка за кариера в AI и технологиите',
          'Етични аспекти и отговорност при използване на AI'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 8,
    title: 'Career & Confidence Lab (16–18 г.)',
    slug: 'career-confidence-lab',
    description: 'Подготовка за кариера и развитие на лични и социални умения, необходими за успех в професионалния свят.',
    seoTitle: 'Курс за кариерно развитие за тийнейджъри | Akava',
    seoDescription: 'Курс за кариерно развитие за тийнейджъри 16-18 г. Научете soft skills, подготовка за интервю и CV създаване. Подкрепа за бъдещата кариера. Записвания отворени!',
    seoH1: 'Курс за кариерно развитие за тийнейджъри (16–18 г.) – Soft Skills и увереност',
    fullDescription: 'Курс за кариерно развитие за тийнейджъри на възраст 16-18 години е идеален избор за тези, които искат да се подготвят за бъдещата си кариера и да развият лични и социални умения. Нашият soft skills курс обхваща основните умения, необходими за успех в професионалния свят. Чрез подготовка за интервю тийнейджърите научават как да се представят уверено и професионално пред потенциални работодатели. Кариерно ориентиране за ученици предоставя необходимите знания за създаване на CV, участие в интервю симулации и развитие на увереност в професионални ситуации.',
    duration: '5 седмици / 5 занятия',
    price: 144,
    formatted_price: '144 €',
    features: [
      'Подготовка за интервю',
      'Soft skills развитие',
      'Комуникация',
      'CV създаване',
      'Интервю симулации',
      'Критическо мислене',
      'Лидерство'
    ],
    learningSections: [
      {
        title: 'Подготовка за интервю и професионално представяне',
        items: [
          'Техники за успешно участие в работни интервюта',
          'Как да се подготвите за различни типове интервюта',
          'Отговори на често задавани въпроси',
          'Невербална комуникация и професионален вид',
          'Как да демонстрирате своите умения и опит'
        ]
      },
      {
        title: 'Soft skills развитие и личностно развитие',
        items: [
          'Развитие на комуникационни умения и ефективна комуникация',
          'Работа в екип и сътрудничество',
          'Развитие на критическо мислене и решаване на проблеми',
          'Управление на времето и организация',
          'Адаптивност и гъвкавост в професионална среда'
        ]
      },
      {
        title: 'Създаване на CV и професионални документи',
        items: [
          'Структуриране и форматиране на професионално CV',
          'Как да представите своите умения и опит ефективно',
          'Създаване на мотивационно писмо',
          'Онлайн профили и LinkedIn оптимизация',
          'Подготовка на портфолио и примери от работата'
        ]
      },
      {
        title: 'Интервю симулации и практика',
        items: [
          'Участие в реалистични интервю симулации',
          'Получаване на обратна връзка и съвети за подобряване',
          'Практикуване на различни сценарии и ситуации',
          'Развитие на увереност и комфорт при интервюта',
          'Преодоляване на страхове и тревожност'
        ]
      },
      {
        title: 'Критическо мислене и решаване на проблеми',
        items: [
          'Развитие на аналитично мислене и анализ на ситуации',
          'Подходи за решаване на сложни проблеми',
          'Приемане на решения в професионална среда',
          'Оценяване на различни перспективи и алтернативи',
          'Прилагане на критическо мислене в реални сценарии'
        ]
      },
      {
        title: 'Лидерски умения и увереност',
        items: [
          'Развитие на лидерски способности и качества',
          'Как да вдъхновявате и мотивирате другите',
          'Развитие на увереност в професионални ситуации'
        ]
      },
      {
        title: 'Кариерно ориентиране и професионално развитие',
        items: [
          'Разбиране на различни кариерни пътища и възможности',
          'Поставяне на кариерни цели и планиране',
          'Подготовка за следващи стъпки в кариерното развитие'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 17,
    title: 'Графичен дизайн - Основи с Adobe (15–18 г.)',
    slug: 'graphic-design-adobe-basics',
    description: 'Практически курс за усвояване на стабилни основи в графичния дизайн и работа с професионалните Adobe инструменти. Курсът развива визуално мислене, композиция и технически умения чрез реални дизайнерски проекти.',
    seoTitle: 'Графичен дизайн курс за ученици | Akava',
    seoDescription: 'Графичен дизайн курс за ученици 15-18 г. Научете Adobe Photoshop, Illustrator, типография и композиция. Практически дизайн курс. Записвания отворени!',
    seoH1: 'Графичен дизайн курс за ученици (15–18 г.) – Adobe Photoshop и Illustrator',
    fullDescription: 'Курсът по графичен дизайн за ученици на възраст 15-18 години обхваща основните принципи на графичния дизайн и работа с професионалните Adobe инструменти. Учениците научават как се създават ефективни дизайни. Програмата включва работа с Photoshop и Illustrator, обработка на изображения, векторни графики и лога, типография и композиция.',
    duration: '10 седмици / 10 занятия',
    price: 250,
    formatted_price: '250 €',
    features: [
      'Основи на графичния дизайн и визуалната комуникация',
      'Цифрова обработка на изображения с Adobe Photoshop',
      'Създаване на векторни графики и лога с Adobe Illustrator',
      'Работа с цветове, типография и композиция',
      'Подготовка на дизайни за дигитална и печатна среда',
      'Практически мини проекти (постер, лого, визия за социални мрежи)'
    ],
    learningSections: [
      {
        title: 'Основи на графичния дизайн и визуалната комуникация',
        items: [
          'Запознаване с графичния дизайн и неговите принципи',
          'Разбиране на визуалната комуникация и нейната роля',
          'Основни концепции на композицията и визуалната йерархия',
          'Дизайн мислене и подход към решаване на визуални задачи'
        ]
      },
      {
        title: 'Цифрова обработка на изображения с Adobe Photoshop',
        items: [
          'Запознаване с Adobe Photoshop интерфейс и инструменти',
          'Работа с растерни изображения и техните формати',
          'Техники за ретуширане и обработка на фотографии',
          'Създаване на композиции и колажи с Photoshop',
          'Подготовка на изображения за печат и дигитална среда'
        ]
      },
      {
        title: 'Създаване на векторни графики и лога с Adobe Illustrator',
        items: [
          'Запознаване с Adobe Illustrator и векторната графика',
          'Работа с пътища, форми и векторни инструменти',
          'Създаване на лога и бранд идентичност',
          'Работа с цветове, градиенти и ефекти',
          'Създаване на илюстрации и векторни графики'
        ]
      },
      {
        title: 'Работа с цветове, типография и композиция',
        items: [
          'Работа с типография и избор на подходящи шрифтове',
          'Принципи на композицията и визуалната йерархия',
          'Баланс, контраст и единство в дизайна',
          'Създаване на визуално привлекателни и ефективни дизайни'
        ]
      },
      {
        title: 'Подготовка на дизайни за дигитална и печатна среда',
        items: [
          'Разлики между дигитален и печатен дизайн',
          'Работа с резолюции, цветови режими и формати',
          'Подготовка на файлове за печат и дигитални платформи',
          'Оптимизация на дизайни за различни устройства и формати',
          'Експортиране и подготовка на финални файлове'
        ]
      },
      {
        title: 'Практически мини проекти',
        items: [
          'Създаване на постер с Adobe Photoshop и Illustrator',
          'Разработване на лого и бранд идентичност',
          'Създаване на визия за социални мрежи',
          'Прилагане на усвоените знания в реални дизайнерски проекти',
          'Изграждане на портфолио от практически дизайнерски проекти'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 18,
    title: 'Графичен дизайн - Портфолио и предпечат (15–18 г.)',
    slug: 'graphic-design-portfolio-prepress',
    description: 'Надграждащ курс за ученици с основни умения по графичен дизайн. Фокус върху предпечат, оформление на многостранични материали и създаване на професионално дизайнерско портфолио.',
    seoTitle: 'Курс по графичен дизайн портфолио | Akava',
    seoDescription: 'Курс по графичен дизайн портфолио за ученици 15-18 г. Научете Adobe InDesign, предпечат и създаване на професионално портфолио. Записвания отворени!',
    seoH1: 'Графичен дизайн - Портфолио и предпечат (15–18 г.)',
    fullDescription: 'Курс по графичен дизайн портфолио и предпечат за ученици на възраст 15-18 години е надграждаща програма за тези, които вече имат основни умения по графичен дизайн.  Обхваща професионални техники за подготовка на дизайни за печат и създаване на завършено дизайнерско портфолио. Adobe InDesign курсът предоставя необходимите знания за работа с професионални инструменти за създаване на брошури, списания и презентационни материали.',
    duration: '6 седмици / 6 занятия',
    price: 165,
    formatted_price: '165 €',
    features: [
      'Работа с Adobe InDesign за многостранични документи',
      'Основи на предпечата и подготовка за печат',
      'Макетиране на брошури, списания и презентационни материали',
      'Изграждане на визуална идентичност',
      'Създаване и подреждане на дизайнерско портфолио',
      'Финален проект с индивидуална обратна връзка'
    ],
    learningSections: [
      {
        title: 'Работа с Adobe InDesign за многостранични документи',
        items: [
          'Запознаване с Adobe InDesign интерфейс и инструменти',
          'Създаване и управление на многостранични документи',
          'Работа с мастър страници и шаблони',
          'Импортиране и форматиране на текст и изображения',
          'Структуриране на сложни документи с множество страници'
        ]
      },
      {
        title: 'Основи на предпечата и подготовка за печат',
        items: [
          'Разбиране на предпечатните процеси и изисквания',
          'Работа с цветови режими (CMYK, RGB) и резолюции',
          'Подготовка на файлове за печат и проверка за грешки',
          'Експортиране на файлове в подходящи формати за печат'
        ]
      },
      {
        title: 'Шаблони на брошури, списания и презентационни материали',
        items: [
          'Създаване на професионални макети за различни типове публикации',
          'Работа с колони, мрежи и визуална йерархия',
          'Макетиране на брошури и каталози',
          'Оформление на списания и многостранични издания',
          'Създаване на презентационни материали и рекламни брошури'
        ]
      },
      {
        title: 'Изграждане на визуална идентичност',
        items: [
          'Разработване на консистентна визуална идентичност',
          'Работа с бранд елементи и стилове',
          'Създаване на шаблони и стандарти за дизайн'
        ]
      },
      {
        title: 'Създаване и подреждане на дизайнерско портфолио',
        items: [
          'Планиране и структуриране на портфолио по графичен дизайн',
          'Добавяне на описания и контекст към проектите',
          'Подготовка на портфолио за различни цели (университет, работа, конкурси)'
        ]
      },
      {
        title: 'Финален проект с индивидуална обратна връзка',
        items: [
          'Разработване на финален дизайнерски проект',
          'Прилагане на всички усвоени техники и знания',
          'Получаване на индивидуална обратна връзка от инструктора',
          'Итеративно подобряване на проекта',
          'Завършване на професионален дизайнерски проект за портфолио'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Присъствен'
  },
  {
    id: 9,
    title: 'QA – Автоматизирано тестване с помощта на AI',
    slug: 'qa-automated-testing',
    description: 'Интензивен курс по автоматизирано тестване за QA начинаещи с Manual основи. Обхваща автоматизация, програмиране, CI/CD и AI технологии в тестовия процес.',
    seoTitle: 'QA автоматизирано тестване курс | Akava',
    seoDescription: 'QA автоматизирано тестване курс за QA инженери с Manual опит. Научете Playwright, Selenium, API тестове и AI инструменти. Automation курс. Записвания отворени!',
    seoH1: 'QA автоматизирано тестване - Automation и AI',
    fullDescription: 'QA автоматизирано тестване курс е подходящ за QA инженери, които вече имат основи по Manual QA и искат да развият умения в автоматизацията. Курсът обхваща всички основни аспекти на автоматизираното тестване, включително работа с Playwright, Selenium и API тестове. Автоматизирано тестване с AI е все по-важно в днешната индустрия, и нашият курс предоставя задълбочено разбиране на AI инструменти като Testim, Mabl и Copilot. В програмата са включени основи на програмирането с JavaScript или Python, ООП концепции, Page Object Model и CI/CD интеграции, което прави обучението още по-приложимо в реални среди.',
    duration: '8 седмици',
    price: 359,
    formatted_price: '359 €',
    features: [
      'Въведение в тестовата автоматизация',
      'Основи на програмирането (JavaScript или Python)',
      'ООП концепции за QA инженери',
      'UI тестова автоматизация (Playwright / Selenium)',
      'Page Object Model и структура на automation проект',
      'API тестова автоматизация',
      'AI инструменти за автоматизирано тестване (Testim, Mabl, Copilot)',
      'CI/CD интеграции и автоматично изпълнение на тестове',
      'Финален automation проект'
    ],
    learningSections: [
      {
        title: 'Въведение в тестовата автоматизация',
        items: [
          'Разбиране на автоматизираното тестване и неговите предимства',
          'Сравнение между Manual и Automation тестване',
          'Кога да се използва автоматизация в тестовия процес',
          'Основни концепции и принципи на test automation',
          'Планиране и стратегия за автоматизация на тестове'
        ]
      },
      {
        title: 'Основи на програмирането за QA – JavaScript или Python',
        items: [
          'Въведение в програмирането за QA инженери',
          'JavaScript за QA: синтаксис, променливи, функции и обекти',
          'Python за QA: основи на езика и работа с библиотеки',
          'Работа с данни, файлове и структури от данни',
          'Обработка на грешки и exception handling',
          'Практически упражнения с програмиране за тестване'
        ]
      },
      {
        title: 'ООП концепции за QA инженери',
        items: [
          'Разбиране на обектно-ориентираното програмиране',
          'Класове, обекти и инстанции',
          'Наследяване и полиморфизъм в тестовата автоматизация',
          'Encapsulation и абстракция',
          'Прилагане на ООП принципи в automation проекти',
          'Best practices за структуриране на automation код'
        ]
      },
      {
        title: 'UI тестова автоматизация – Playwright и Selenium',
        items: [
          'Запознаване с Playwright и неговите възможности',
          'Работа с Selenium WebDriver за UI автоматизация',
          'Локализиране на елементи и взаимодействие с уеб интерфейси',
          'Синхронизация и очаквания в автоматизираните тестове',
          'Работа с различни браузъри и платформи',
          'Handling на alerts, popups и iframes',
          'Скрийншоти и видео записване при тестване'
        ]
      },
      {
        title: 'Page Object Model и структура на automation проект',
        items: [
          'Разбиране на Page Object Model (POM) pattern',
          'Създаване на page objects и page classes',
          'Структуриране на automation проект за поддръжка',
          'Организация на тестове, конфигурации и данни',
          'Best practices за код организация и reusability',
          'Създаване на scalable и maintainable automation framework'
        ]
      },
      {
        title: 'API тестова автоматизация',
        items: [
          'Въведение в API тестване и неговото значение',
          'Работа с REST API и HTTP методи',
          'Създаване на API тестове с JavaScript или Python',
          'Валидация на responses и status codes',
          'Работа с JSON данни и parsing',
          'API тестване с инструменти като Postman и REST Assured',
          'Интеграция на API тестове в automation framework'
        ]
      },
      {
        title: 'AI инструменти за автоматизирано тестване',
        items: [
          'Въведение в AI технологиите в тестовата автоматизация',
          'Работа с Testim за AI-powered тестване',
          'Използване на Mabl за интелигентна автоматизация',
          'GitHub Copilot за помощ в писането на automation код',
          'AI-assisted test generation и maintenance',
          'Предимства и ограничения на AI инструментите',
          'Интеграция на AI инструменти в тестовия процес'
        ]
      },
      {
        title: 'CI/CD интеграции и автоматично изпълнение на тестове',
        items: [
          'Разбиране на CI/CD и неговото значение в QA',
          'Интеграция на automation тестове в CI/CD pipeline',
          'Работа с Jenkins, GitHub Actions или други CI/CD инструменти',
          'Автоматично изпълнение на тестове при code changes',
          'Test reporting и notification системи',
          'Best practices за CI/CD интеграция на тестове',
          'Мониторинг и анализ на резултатите от автоматизираните тестове'
        ]
      },
      {
        title: 'Финален automation проект',
        items: [
          'Разработване на пълнофункционален automation проект',
          'Прилагане на всички усвоени техники и знания',
          'Създаване на UI и API тестове за реално приложение',
          'Интеграция на AI инструменти в проекта',
          'CI/CD настройка и автоматично изпълнение',
          'Документиране на проекта и код review',
          'Демонстрация на проекта и получаване на обратна връзка'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 16,
    title: 'QA - Ръчно тестване',
    slug: 'qa-manual-testing',
    description: 'Комплексен практически курс по ръчно тестване на софтуер, подходящ за напълно начинаещи, които искат да направят първи стъпки като Manual QA Engineer.',
    seoTitle: 'QA ръчно тестване курс за начинаещи | Akava',
    seoDescription: 'QA ръчно тестване курс за напълно начинаещи. Научете основите на софтуерното тестване, тестови сценарии и bug reports. Manual QA курс. Записвания отворени!',
    seoH1: 'QA manual testing - Софтуерно тестване за начинаещи',
    fullDescription: 'QA manual testing е подходящ за хора без предишен опит, които искат да започнат кариера в софтуерното тестване. Manual QA курсът обхваща всички основни концепции и практики на ръчното тестване на софтуер. Програмата включва идентифициране и документиране на проблеми в софтуера, създаване на тестови сценарии и тестови случаи, работа с професионални инструменти като Jira.',
    duration: '6 седмици',
    price: 282,
    formatted_price: '282 €',
    features: [
      'Основи на софтуерното тестване',
      'Типове тестове и техники за тест-дизайн',
      'Тестови сценарии и тестови случаи',
      'Изготвяне на бъг репорт',
      'Работа с инструменти като Jira',
      'QA процеси и документация',
      'Практически мини проект'
    ],
    learningSections: [
      {
        title: 'Основи на софтуерното тестване',
        items: [
          'Разбиране на какво е софтуерно тестване и защо е важно',
          'Ролята на QA инженера в софтуерния процес',
          'Основни концепции: bug, defect, error, failure',
          'Принципи на тестването и неговите цели',
          'Разлика между Manual и Automation тестване',
          'Първи стъпки в света на QA тестването'
        ]
      },
      {
        title: 'Типове тестове и техники за тест-дизайн',
        items: [
          'Функционално и нефункционално тестване',
          'Black box, White box и Gray box тестване',
          'Техники за тест-дизайн: еквивалентни класове, гранични стойности',
          'Тестване на позитивни и негативни сценарии',
          'Smoke, Sanity, Regression и други типове тестове',
          'Прилагане на техники за тест-дизайн в практиката'
        ]
      },
      {
        title: 'Тестови сценарии и тестови случаи',
        items: [
          'Как да създавате ясни и структурирани тестови сценарии',
          'Писане на тестови случаи',
          'Структура на тестовия случай: precondition, steps, expected result',
          'Приоритизиране на тестовите случаи',
          'Организация и поддръжка на тестова документация',
          'Best practices за създаване на ефективни тестови случаи'
        ]
      },
      {
        title: 'Изготвяне на bug report',
        items: [
          'Разпознаване и документиране на bugs',
          'Структура на професионален bug report',
          'Важни елементи: summary, steps to reproduce, expected vs actual result',
          'Приоритизиране на bugs по сериозност и приоритет',
          'Скрийншоти, логове и други доказателства',
          'Комуникация с разработчиците относно bugs',
          'Проследяване на bug lifecycle'
        ]
      },
      {
        title: 'Работа с инструменти като Jira',
        items: [
          'Запознаване с Jira интерфейс и функционалности',
          'Създаване и управление на тестови задачи в Jira',
          'Създаване на bug reports в Jira',
          'Работа с тестови планове и тестови изпълнения',
          'Използване на Jira за проследяване на тестов процес',
          'Best practices за работа с Jira в QA контекст'
        ]
      },
      {
        title: 'QA процеси и документация',
        items: [
          'Разбиране на QA процесите в софтуерния development lifecycle',
          'Тестова документация: тест планове, тест стратегии',
          'Тестова среда и тестови данни',
          'Работа в екип и комуникация с разработчици и stakeholders',
          'QA метрики и reporting',
          'Best practices за QA процеси и документация'
        ]
      },
      {
        title: 'Практически QA проект',
        items: [
          'Прилагане на всички усвоени знания в реален проект',
          'Създаване на тестови сценарии и тестови случаи за приложение',
          'Извършване на тестване и документиране на bugs',
          'Работа с Jira за управление на тестов процес',
          'Създаване на QA документация',
          'Получаване на обратна връзка и подобряване на уменията',
          'Подготовка на портфолио от QA работа'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 10,
    title: 'Project Management в ИТ',
    slug: 'project-management-it',
    description: 'Обучение по Agile, Scrum и Kanban, управление на екипи, работа с професионални инструменти и ефективна комуникация в IT среда.',
    seoTitle: 'Курс по Project Management в ИТ | Akava',
    seoDescription: 'Курс по Project Management в ИТ. Научете Agile, Scrum, Kanban, управление на екипи и работа с JIRA/Trello. IT Project Manager обучение. Записвания отворени!',
    seoH1: 'Курс по Project Management в ИТ – Agile, Scrum и Kanban',
    fullDescription: 'Курс по Project Management в ИТ е идеален избор за тези, които искат да развият умения за управление на IT проекти и екипи. Курсът обхваща основните методологии и практики, използвани в софтуерната индустрия. Управление на IT проекти изисква комбинация от технически знания и лидерски умения, които се развиват чрез практически упражнения и работа с професионални инструменти като JIRA и Trello.',
    duration: '6 седмици',
    price: 333,
    formatted_price: '333 €',
    features: [
      'Agile методологии',
      'Scrum и Kanban',
      'Управление на екипи',
      'Инструменти (JIRA, Trello)',
      'Бюджетиране',
      'Комуникация и лидерство',
      'Подготовка за IT Project Manager'
    ],
    learningSections: [
      {
        title: 'Agile методологии и принципи',
        items: [
          'Запознаване с Agile философията и основните принципи',
          'Разбиране на Agile манифеста и неговите стойности',
          'Сравнение на Agile с традиционните методологии',
          'Прилагане на Agile принципи в IT проекти',
          'Agile практики и техники за успешно управление'
        ]
      },
      {
        title: 'Scrum методология',
        items: [
          'Разбиране на Scrum framework и неговите компоненти',
          'Scrum роли: Product Owner, Scrum Master, Development Team',
          'Scrum събития: Sprint Planning, Daily Standup, Sprint Review, Retrospective',
          'Scrum артефакти: Product Backlog, Sprint Backlog, Increment',
          'Практическо прилагане на Scrum в IT проекти'
        ]
      },
      {
        title: 'Kanban методология',
        items: [
          'Въведение в Kanban и неговите принципи',
          'Създаване и управление на Kanban дъски',
          'Work in Progress (WIP) ограничения и оптимизация',
          'Flow management и continuous delivery',
          'Прилагане на Kanban в различни IT контексти'
        ]
      },
      {
        title: 'Работа с професионални инструменти – JIRA и Trello',
        items: [
          'Запознаване с JIRA интерфейс и функционалности',
          'Създаване и управление на проекти в JIRA',
          'Работа с issues, epics, stories и tasks',
          'Използване на Trello за управление на проекти',
          'Интеграция на инструменти в работния процес',
          'Best practices за използване на JIRA и Trello'
        ]
      },
      {
        title: 'Управление на екипи в IT среда',
        items: [
          'Техники за ефективно управление на разработчици и екипи',
          'Мотивиране и вдъхновяване на екипи',
          'Разрешаване на конфликти и управление на динамиката',
          'Разпределение на задачи и ресурси',
          'Performance management и feedback',
          'Създаване на продуктивна и позитивна работна среда'
        ]
      },
      {
        title: 'Бюджетиране и планиране на проекти',
        items: [
          'Оценяване на време и ресурси за IT проекти',
          'Създаване на проектен бюджет и проследяване на разходи',
          'Risk management и идентифициране на рискове',
          'Планиране на проекти и timeline управление',
          'Адаптация на плановете при промени',
          'Reporting и комуникация на прогрес към стейкхолдъри'
        ]
      },
      {
        title: 'Комуникация и лидерство в IT управлението',
        items: [
          'Ефективна комуникация с всички заинтересовани страни на проекта',
          'Провеждане на ефективни срещи и презентации',
          'Лидерски умения и техники за мотивиране',
          'Комуникация в кризисни ситуации',
          'Създаване на ясна визия и комуникиране на цели',
          'Развитие на емоционална интелигентност в управлението'
        ]
      },
      {
        title: 'Роли в IT управлението и кариерни пътища',
        items: [
          'Разбиране на различните роли: IT Project Manager, Scrum Master, Product Owner',
          'Изисквания и отговорности за различните роли',
          'Как да се подготвите за IT Project Manager позиции',
          'Сертификации и допълнителни обучения',
          'Развитие на кариера в IT управлението',
          'Best practices и съвети за успех в ролята'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 11,
    title: 'Софтуерно инженерство – Full Stack старт',
    slug: 'full-stack-start',
    description: 'Стабилен старт за кариера във Full Stack разработката чрез изграждане на основни технически умения, реални проекти и технологична база за Junior позиции.',
    seoTitle: 'Full Stack курс за начинаещи | Akava',
    seoDescription: 'Full Stack курс за начинаещи. Научете HTML, CSS, JavaScript, backend разработка и бази данни. Софтуерно инженерство курс с реални проекти. Записвания отворени!',
    seoH1: 'Full Stack курс за начинаещи – Софтуерно инженерство',
    fullDescription: 'Full Stack курс за начинаещи обхваща основни технологии за създаване на уеб приложения от начало до край и развива технически умения за подготовка за Junior Full Stack Developer позиции. Курсът включва работа с HTML, CSS, JavaScript за фронтенд, както и backend разработка с Node.js, Python или Java, работа с REST API, бази данни, deployment и DevOps основи и създаване на портфолио проекти',
    duration: '14 седмици',
    price: 667,
    formatted_price: '667 €',
    features: [
      'HTML/CSS и JavaScript',
      'Backend разработка (Node.js/Java/Python)',
      'REST API',
      'Работа с база данни',
      'Deployment и DevOps основи',
      'Портфолио проекти',
      'Подготовка за Junior Full-Stack Developer'
    ],
    learningSections: [
      {
        title: 'HTML, CSS и JavaScript – Фронтенд основи',
        items: [
          'Запознаване с HTML структура и семантични елементи',
          'CSS стилизиране, layout и responsive дизайн',
          'JavaScript основи: променливи, функции, обекти и масиви',
          'DOM манипулация и интерактивност',
          'Съвременни JavaScript техники и ES6+ функции',
          'Работа с асинхронен код и API заявки'
        ]
      },
      {
        title: 'Backend разработка – Node.js, Python или Java',
        items: [
          'Въведение в backend разработката и сървърна архитектура',
          'Работа с Node.js и Express за сървърни приложения',
          'Backend разработка с Python (Flask/Django)',
          'Backend разработка с Java (Spring Boot)',
          'Създаване на RESTful API endpoints',
          'Обработка на заявки, валидация и error handling'
        ]
      },
      {
        title: 'REST API и комуникация между фронтенд и backend',
        items: [
          'Разбиране на REST архитектурата и принципите',
          'Създаване на REST API endpoints',
          'HTTP методи, статус кодове и headers',
          'Интеграция на фронтенд с backend API',
          'Работа с JSON данни и сериализация',
          'API документация и тестване'
        ]
      },
      {
        title: 'Работа с бази данни',
        items: [
          'Въведение в базите данни и SQL',
          'Работа с релационни бази данни (MySQL, PostgreSQL)',
          'NoSQL бази данни (MongoDB)',
          'ORM и query builders',
          'Database migrations и schema management',
          'Оптимизация на заявки и индексиране'
        ]
      },
      {
        title: 'Deployment и DevOps основи',
        items: [
          'Подготовка на приложения за production',
          'Deployment на уеб приложения (Heroku, AWS, Vercel)',
          'Работа с Git и version control',
          'CI/CD основи и автоматизация',
          'Мониторинг и логване на приложения',
          'Основни DevOps практики и инструменти'
        ]
      },
      {
        title: 'Портфолио проекти и практически приложения',
        items: [
          'Разработване на пълнофункционални уеб приложения',
          'Създаване на портфолио проекти, демонстриращи уменията',
          'Прилагане на всички усвоени технологии в реални проекти',
          'Code review и best practices',
          'Документиране на проекти и код',
          'Подготовка на проекти за показване на потенциални работодатели'
        ]
      },
      {
        title: 'Подготовка за Junior Full Stack Developer позиции',
        items: [
          'Разбиране на изискванията за Junior позиции',
          'Как да се подготвите за технически интервюта',
          'Създаване на професионално портфолио',
          'Best practices в софтуерното инженерство',
          'Работа в екип и collaboration инструменти',
          'Следващи стъпки в кариерното развитие'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 12,
    title: 'UI/UX дизайн с Figma - Основи и практическо приложение (+ AI бонус)',
    slug: 'ai-driven-ui-design',
    description: 'Практически курс по UI/UX дизайн и работа с Figma. Подходящ за начинаещи и напреднали, които искат да създават функционални и визуално издържани потребителски интерфейси според добрите практики в индустрията.',
    seoTitle: 'UI UX дизайн курс с Figma | Akava',
    seoDescription: 'UI UX дизайн курс с Figma за начинаещи. Научете UX процес, wireframes, прототипиране и AI инструменти. Figma курс с практика. Записвания отворени!',
    seoH1: 'UI UX дизайн курс с Figma – UX процес и визуален дизайн',
    fullDescription: 'UI UX дизайн с Figma е идеален избор за тези, които искат да научат професионални техники за създаване на потребителски интерфейси. Курсът обхваща всички основни аспекти на UI/UX дизайна, от UX процес и user research до визуален дизайн и прототипиране на уеб и мобилни интерфейси. Програмата включва user research, user flows и wireframes, принципи на потребителското изживяване, работа с цветове, типография, визуална йерархия и UI компоненти.',
    duration: '8 седмици / 8 занятия',
    price: 385,
    formatted_price: '385 €',
    features: [
      'Основи на UI и UX дизайна',
      'Принципи на потребителското изживяване',
      'UX процес: research, user flows, wireframes',
      'Основи на визуалния UI дизайн',
      'Цветове, типография и визуална йерархия',
      'Работа с Figma – интерфейс и инструменти',
      'Създаване на wireframes и UI компоненти',
      'Прототипиране и интерактивни екрани',
      'Добри практики при дизайн на уеб и мобилни интерфейси',
      'Практически задания и реални UI проекти',
      'Изграждане на портфолио от UI/UX проекти',
      'Въведение в AI инструменти за дизайн',
      'AI подпомагане при wireframes и прототипи',
      'Генериране на идеи и UI компоненти с AI',
      'Ускоряване на дизайн процеса',
      'Добри практики и етика при използване на AI в дизайна'
    ],
    learningSections: [
      {
        title: 'UX дизайн – Процес и принципи',
        items: [
          'Разбиране на UX дизайна и неговото значение',
          'UX процес: от research до implementation',
          'User research техники и методи',
          'Създаване на user personas и user stories',
          'User flows и customer journey mapping',
          'Принципи на потребителското изживяване',
          'Usability и accessibility принципи'
        ]
      },
      {
        title: 'Wireframes и структуриране на информация',
        items: [
          'Създаване на wireframes за различни типове интерфейси',
          'Информационна архитектура и навигация',
          'Layout и визуална йерархия в wireframes',
          'Low-fidelity и high-fidelity wireframes',
          'Итеративно подобряване на wireframes',
          'Best practices за wireframing'
        ]
      },
      {
        title: 'UI дизайн – Визуален дизайн и компоненти',
        items: [
          'Основи на визуалния UI дизайн',
          'Работа с цветове и цветови палитри',
          'Типография и избор на подходящи шрифтове',
          'Визуална йерархия и композиция',
          'Създаване на UI компоненти и дизайн системи',
          'Иконография и визуални елементи',
          'Responsive дизайн за различни устройства'
        ]
      },
      {
        title: 'Работа с Figma – Интерфейс и инструменти',
        items: [
          'Запознаване с Figma интерфейс и основни функции',
          'Работа с frames, layers и groups',
          'Figma инструменти за дизайн и редактиране',
          'Създаване и управление на компоненти и стилове',
          'Figma plugins и интеграции',
          'Collaboration и споделяне в Figma'
        ]
      },
      {
        title: 'Прототипиране и интерактивни екрани',
        items: [
          'Създаване на интерактивни прототипи в Figma',
          'Настройване на interactions и transitions',
          'Тестване на прототипи и събиране на обратна връзка',
          'Подготовка на прототипи за презентация'
        ]
      },
      {
        title: 'Дизайн на уеб и мобилни интерфейси',
        items: [
          'Разлики между уеб и мобилен дизайн',
          'Добри практики за дизайн на уеб интерфейси',
          'Mobile-first подход и responsive дизайн',
          'Дизайн за различни екрани и резолюции',
          'Touch interactions и mobile patterns',
          'Best practices за дизайн на уеб и мобилни приложения'
        ]
      },
      {
        title: 'Практически проекти и портфолио',
        items: [
          'Разработване на реални UI/UX проекти',
          'Прилагане на всички усвоени техники и знания',
          'Създаване на портфолио от UI/UX проекти',
          'Презентиране на проекти и получаване на обратна връзка',
          'Best practices за портфолио по UI UX',
          'Подготовка за следващи стъпки в дизайн кариерата'
        ]
      },
      {
        title: 'AI инструменти за дизайн – Бонус модул',
        items: [
          'Въведение в AI инструменти за дизайн',
          'AI за Figma и интеграция в работния процес',
          'AI подпомагане при създаване на wireframes и прототипи',
          'Генериране на идеи и UI компоненти с AI',
          'Ускоряване на дизайн процеса с AI помощ',
          'Дизайн с изкуствен интелект – възможности и ограничения',
          'Добри практики и етика при използване на AI в дизайна',
          'Балансиране между AI помощ и дизайнерска креативност'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 19,
    title: 'Графичен дизайн – Професионална подготовка',
    slug: 'graphic-design-professional-adobe',
    description: 'Професионален курс по графичен дизайн с фокус върху реални умения, търсени на пазара на труда. Подходящ за възрастни, които искат да се преквалифицират или да надградят своите дизайнерски умения.',
    seoTitle: 'Курс по графичен дизайн за възрастни | Akava',
    seoDescription: 'Курс по графичен дизайн за възрастни. Професионален курс с Adobe Photoshop, Illustrator и AI инструменти. Графичен дизайн преквалификация. Записвания отворени!',
    seoH1: 'Графичен дизайн – Професионална подготовка',
    fullDescription: 'Курсът по графичен дизайн обхваща всички ключови аспекти на визуалната комуникация и работа с Adobe инструменти. Програмата предоставя практически знания за работа с Adobe Photoshop и Adobe Illustrator.',
    duration: '12 седмици / 12 занятия',
    price: 501,
    formatted_price: '501 €',
    features: [
      'Основи на графичния дизайн и визуалната комуникация',
      'Цветове, композиция и визуална йерархия',
      'Adobe Photoshop – растерна графика и обработка на изображения',
      'Adobe Illustrator – векторни графики, лога и илюстрации',
      'Типография и визуална идентичност',
      'Практически задания по реални казуси',
      'Подготовка на дизайнерски проекти',
      'Работа с AI инструменти в графичния дизайн (Adobe Firefly, Canva AI)',
      'Генериране на идеи и ускоряване на дизайнерския процес',
      'Добри практики и етика при използване на AI'
    ],
    learningSections: [
      {
        title: 'Основи на графичния дизайн и визуалната комуникация',
        items: [
          'Разбиране на графичния дизайн и неговото значение',
          'Основни принципи на дизайна и композицията',
          'Визуална йерархия и как да я прилагаме',
          'Разбиране на целевата аудитория и контекста',
          'Дизайн мислене и подход към решаване на визуални задачи'
        ]
      },
      {
        title: 'Цветове, композиция и визуална йерархия',
        items: [
          'Работа с цветови палитри и контрасти',
          'Композиция и баланс в дизайна',
          'Визуална йерархия и фокусни точки',
          'Прилагане на принципите в различни типове проекти'
        ]
      },
      {
        title: 'Adobe Photoshop – Растерна графика и обработка на изображения',
        items: [
          'Запознаване с Adobe Photoshop интерфейс и инструменти',
          'Работа с растерни изображения и техните формати',
          'Техники за ретуширане и обработка на фотографии',
          'Създаване на композиции и колажи',
          'Работа със слоеве, маски и филтри',
          'Подготовка на изображения за печат и дигитална среда',
          'Advanced техники в Photoshop'
        ]
      },
      {
        title: 'Adobe Illustrator – Векторни графики, лога и илюстрации',
        items: [
          'Запознаване с Adobe Illustrator и векторната графика',
          'Работа с пътища, форми и векторни инструменти',
          'Създаване на лога и бранд идентичност',
          'Работа с цветове, градиенти и ефекти',
          'Създаване на илюстрации и векторни графики',
          'Типография в Illustrator',
          'Advanced техники в Illustrator'
        ]
      },
      {
        title: 'Типография и визуална идентичност',
        items: [
          'Разбиране на типографията и нейната важност',
          'Избор на подходящи шрифтове за различни проекти',
          'Работа с типографска йерархия',
          'Създаване на визуална идентичност и бранд',
          'Прилагане на визуална идентичност в различни материали',
          'Best practices за типография и визуална идентичност'
        ]
      },
      {
        title: 'Практически проекти и реални дизайнерски казуси',
        items: [
          'Работа по реални дизайнерски казуси',
          'Създаване на дизайнерски проекти от концепция до финал',
          'Прилагане на всички усвоени техники и знания',
          'Получаване на обратна връзка и итеративно подобряване',
          'Създаване на портфолио от дизайнерски проекти',
          'Подготовка на проекти за клиенти и работодатели'
        ]
      },
      {
        title: 'AI в графичния дизайн – Инструменти и етика',
        items: [
          'Въведение в AI инструменти за графичен дизайн',
          'Работа с Adobe Firefly за генериране на изображения',
          'Използване на Canva AI за дизайн помощ',
          'Дизайн с изкуствен интелект – възможности и ограничения',
          'Генериране на идеи и ускоряване на дизайнерския процес',
          'Етика при използване на AI в дизайна',
          'Балансиране между AI помощ и дизайнерска креативност',
          'Best practices за интеграция на AI в работния процес'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 20,
    title: 'Графичен дизайн - Предпечат и бранд материали с Adobe InDesign',
    slug: 'graphic-design-prepress-brand-materials',
    description: 'Надграждащ курс за предпечатна подготовка и създаване на бранд материали. Подходящ за курсисти с основни познания по графичен дизайн.',
    duration: '6 седмици / 6 занятия',
    price: 266,
    formatted_price: '266 €',
    features: [
      'Работа с Adobe InDesign',
      'Подготовка на файлове за печат',
      'Цветови режими, резолюции и формати',
      'Създаване на брошури, каталози и рекламни материали',
      'Многостранични документи и layout',
      'Основи на предпечатната подготовка',
      'AI инструменти за автоматизация на дизайн и предпечат',
      'Оптимизация на работния процес'
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 13,
    title: 'Digital Essentials – Дигитални компетентности за съвременния офис',
    slug: 'digital-essentials',
    description: 'Практически курс за развитие на ключови дигитални компетентности, необходими за ефективна работа в съвременна офис среда.',
    seoTitle: 'Дигитални компетентности курс | Akava',
    seoDescription: 'Дигитални компетентности курс за възрастни. Научете Microsoft Office, онлайн комуникация и киберсигурност. Офис умения курс. Записвания отворени!',
    seoH1: 'Дигитални компетентности - Офис умения и Microsoft Office',
    fullDescription: 'Курсът по дигитални компетентности е подходящ за възрастни и офис служители, които искат да развият своите дигитални умения. Програмата обхваща всички ключови компетентности, необходими за ефективна работа в съвременна офис среда - практически обучение по Microsoft Office обучение, включително Microsoft Word, Excel, PowerPoint и Outlook, работа с OneDrive и Google Drive за организация на файлове, онлайн комуникационни инструменти като Teams, Zoom и Slack, основи на киберсигурността и дигитална етика.',
    duration: '6 седмици / 6 занятия',
    price: 215,
    formatted_price: '215 €',
    features: [
      'Microsoft Office (Word, Excel, PowerPoint, Outlook)',
      'Организация и управление на файлове (OneDrive, Google Drive)',
      'Онлайн комуникация (Teams, Zoom, Slack)',
      'Основи на киберсигурността',
      'Дигитална етика',
      'Практически офис умения'
    ],
    learningSections: [
      {
        title: 'Microsoft Office – Word, Excel, PowerPoint и Outlook',
        items: [
          'Microsoft Word: форматиране на документи, таблици и стилове',
          'Microsoft Excel: работа с таблици, формули, графики и pivot tables',
          'Microsoft PowerPoint: създаване на професионални презентации',
          'Microsoft Outlook: управление на имейли, календар и контакти',
          'Практически техники и shortcuts за по-бърза работа',
          'Създаване на професионални документи и отчети'
        ]
      },
      {
        title: 'Организация и управление на файлове – OneDrive и Google Drive',
        items: [
          'Работа с OneDrive за синхронизация и споделяне на файлове',
          'Използване на Google Drive за съхранение и организация',
          'Създаване на папки и структуриране на документи',
          'Споделяне на файлове и работа в екип',
          'Backup и защита на важни документи',
          'Достъп до файлове от различни устройства'
        ]
      },
      {
        title: 'Онлайн комуникация – Teams, Zoom и Slack',
        items: [
          'Провеждане на видеоконференции с Teams и Zoom',
          'Управление на виртуални срещи и календар',
          'Работа със Slack за екипна комуникация',
          'Споделяне на екран и съдържание по време на срещи',
          'Чат комуникация и групови разговори',
          'Best practices за ефективна онлайн комуникация'
        ]
      },
      {
        title: 'Основи на киберсигурността',
        items: [
          'Разбиране на основните заплахи в дигиталния свят',
          'Създаване на силни пароли и управление на акаунти',
          'Разпознаване на фишинг и измами',
          'Защита на лични и служебни данни',
          'Безопасно използване на интернет и имейл',
          'Best practices за киберсигурност в офис среда'
        ]
      },
      {
        title: 'Дигитална етика и професионално поведение',
        items: [
          'Етични принципи при използване на дигитални инструменти',
          'Професионална комуникация онлайн',
          'Управление на дигитален отпечатък',
          'Права и отговорности в дигиталната среда',
          'Конфиденциалност и защита на данни',
          'Създаване на позитивна онлайн репутация'
        ]
      },
      {
        title: 'Практически офис умения и ефективност',
        items: [
          'Техники за по-ефективна работа с дигитални инструменти',
          'Автоматизация на повтарящи се задачи',
          'Организация на работното пространство и файлове',
          'Time management с дигитални инструменти',
          'Създаване на шаблони и стандарти за работа',
          'Прилагане на усвоените умения в реални офис ситуации'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  },
  {
    id: 14,
    title: 'Vibe Coding - Програмиране с AI',
    slug: 'vibe-coding',
    description: 'Уникален курс по програмиране чрез с помощта на инструменти с изкуствен интелект. Подходящ за хора без опит, които искат да навлязат в IT сферата по нестандартен и вдъхновяващ начин.',
    seoTitle: 'Програмиране с AI курс за начинаещи | Akava',
    seoDescription: 'Програмиране с AI курс за начинаещи. Научете програмиране с изкуствен интелект чрез творчески проекти и визуализации. Vibe Coding курс. Записвания отворени!',
    seoH1: 'Vibe Coding - Програмиране с AI',
    fullDescription: 'Програмиране с AI е подходящ за хора без предишен опит. Програмата на курса включва работа с AI code асистенти, които помагат при структуриране на код и създаване на интерактивни приложения.',
    duration: '8 седмици / 8 занятия',
    price: 282,
    formatted_price: '282 €',
    features: [
      'Основи на програмирането чрез визуално и звуково възприятие',
      'Креативно мислене и структуриране на код',
      'Програмиране на интерактивни приложения',
      'Визуализации и творчески проекти',
      'Работа с Processing и p5.js',
      'AI code асистенти',
      'Креативни програмни проекти'
    ],
    learningSections: [
      {
        title: 'Основи на програмирането чрез визуално и звуково възприятие',
        items: [
          'Разбиране на основните концепции на програмирането',
          'Интуитивен подход към структурирането на код'
        ]
      },
      {
        title: 'Програмиране на интерактивни приложения',
        items: [
          'Създаване на интерактивни приложения от нулата',
          'Работа с потребителски вход и взаимодействия',
          'Реагиране на събития и потребителски действия',
          'Интеграция на различни елементи в едно приложение',
          'Тестване и подобряване на интерактивността'
        ]
      },
      {
        title: 'AI code асистенти и програмиране с AI',
        items: [
          'Използване на AI code асистенти за помощ в програмирането',
          'Работа с AI инструменти за генериране на код',
          'Как AI може да помогне при структуриране на код',
          'Интеграция на AI в творческия процес на програмиране',
          'Best practices за използване на AI в програмирането'
        ]
      },
      {
        title: 'Креативни програмни проекти',
        items: [
          'Разработване на собствени творчески програмни проекти',
          'Прилагане на всички усвоени техники и знания',
          'Създаване на портфолио от креативни проекти',
          'Презентиране на проекти и получаване на обратна връзка'
        ]
      }
    ],
    status: 'Предстоящ',
    format: 'Онлайн'
  }
];

const CourseRegistration: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  
  // Get stored category and subcategory for back navigation
  const storedCategory = sessionStorage.getItem('courseCategory');
  const storedSubcategory = sessionStorage.getItem('courseSubcategory');
  
  // Build back URL with category and subcategory if available
  const getBackUrl = () => {
    if (storedCategory) {
      let url = `/courses/${storedCategory}`;
      if (storedSubcategory) {
        url += `/${storedSubcategory}`;
      }
      return url;
    }
    return '/courses';
  };
  
  const [course, setCourse] = useState(allCourses.find(c => c.slug === slug) || null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const serviceId = (process.env.REACT_APP_EMAILJS_SERVICE_ID || '').trim();
  const templateId = (process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '').trim();
  const publicKey = (process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '').trim();

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation function (Bulgarian format: 0XXXXXXXX or +359XXXXXXXXX)
  const validatePhone = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional
    // Remove spaces, dashes, and parentheses
    const cleaned = phone.replace(/[\s\-()]/g, '');
    // Check for Bulgarian format: starts with 0 and 9 digits, or +359 and 9 digits
    const phoneRegex = /^(\+359|0)[0-9]{9}$/;
    return phoneRegex.test(cleaned);
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setEmailError('Моля, въведете валиден имейл адрес');
    } else {
      setEmailError(null);
    }
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError('Моля, въведете валиден телефонен номер (напр. 0895123456 или +359895123456)');
    } else {
      setPhoneError(null);
    }
  };

  useEffect(() => {
    if (slug) {
      const foundCourse = allCourses.find(c => c.slug === slug);
      if (foundCourse) {
        setCourse(foundCourse);
      }
    }
  }, [slug]);

  // Set SEO meta tags
  useEffect(() => {
    if (course) {
      const seoTitle = (course as any).seoTitle || `${course.title} | Akava Academy`;
      const seoDescription = (course as any).seoDescription || course.description;
      const baseUrl = 'https://www.akavaacademy.com';
      const canonicalUrl = `${baseUrl}/course/${course.slug}`;

      // Update document title
      document.title = seoTitle;

      // Update or create canonical link
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonicalUrl);

      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', seoDescription);

      let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'index, follow');

      const ogTitleSelector = 'meta[property="og:title"]';
      let ogTitle = document.querySelector(ogTitleSelector) as HTMLMetaElement | null;
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', seoTitle);

      const ogDescSelector = 'meta[property="og:description"]';
      let ogDesc = document.querySelector(ogDescSelector) as HTMLMetaElement | null;
      if (!ogDesc) {
        ogDesc = document.createElement('meta');
        ogDesc.setAttribute('property', 'og:description');
        document.head.appendChild(ogDesc);
      }
      ogDesc.setAttribute('content', seoDescription);

      const ogUrlSelector = 'meta[property="og:url"]';
      let ogUrl = document.querySelector(ogUrlSelector) as HTMLMetaElement | null;
      if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.appendChild(ogUrl);
      }
      ogUrl.setAttribute('content', canonicalUrl);

      const ogTypeSelector = 'meta[property="og:type"]';
      let ogType = document.querySelector(ogTypeSelector) as HTMLMetaElement | null;
      if (!ogType) {
        ogType = document.createElement('meta');
        ogType.setAttribute('property', 'og:type');
        document.head.appendChild(ogType);
      }
      ogType.setAttribute('content', 'product');

      const ogImageSelector = 'meta[property="og:image"]';
      let ogImage = document.querySelector(ogImageSelector) as HTMLMetaElement | null;
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', 'https://www.akavaacademy.com/logo512.png');

      const schemaId = 'akava-course-schema';
      let schemaScript = document.getElementById(schemaId) as HTMLScriptElement | null;
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = schemaId;
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }

      const courseSchema: any = {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: course.title,
        description: seoDescription,
        inLanguage: 'bg',
        provider: {
          '@type': 'EducationalOrganization',
          name: 'Акава Академи',
          url: 'https://www.akavaacademy.com/'
        }
      };

      if (course.formatted_price) {
        courseSchema.hasCourseInstance = {
          '@type': 'CourseInstance',
          courseMode: course.format || 'офлайн',
          name: course.title,
        };
      }

      schemaScript.text = JSON.stringify(courseSchema);

      (window as any).gtag?.('event', 'view_item', {
        item_id: course.slug,
        item_name: course.title,
        item_category: 'Course',
      });

      (window as any).fbq?.('track', 'ViewContent', {
        content_name: course.title,
        content_category: 'Course',
        content_ids: [course.slug],
        content_type: 'product',
      });
    }
  }, [course]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear errors when user starts typing
    if (e.target.name === 'email' && emailError) {
      setEmailError(null);
    }
    if (e.target.name === 'phone' && phoneError) {
      setPhoneError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setEmailError(null);
    setPhoneError(null);
    
    // Validate email before submitting
    if (!formData.email) {
      setEmailError('Имейл адресът е задължителен');
      setIsSubmitting(false);
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setEmailError('Моля, въведете валиден имейл адрес');
      setIsSubmitting(false);
      return;
    }
    
    // Validate phone if provided
    if (!formData.phone) {
      setEmailError('Телефонен номер е задължителен');
      setIsSubmitting(false);
      return;
    }
    
    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError('Моля, въведете валиден телефонен номер (напр. 0895123456 или +359895123456)');
      setIsSubmitting(false);
      return;
    }
    
    try {
      if (!serviceId || !templateId || !publicKey) {
        throw new Error('EmailJS не е конфигуриран. Моля, проверете environment variables.');
      }

      const templateParams = {
        to_email: 'info@akavaacademy.com',
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Не е предоставен',
        course: course?.title || 'Не е избран',
        message: formData.message || 'Няма допълнителна информация',
        subject: course?.title 
          ? `Заявка за записване - ${course?.title}` 
          : 'Ново запитване от контактна форма'
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);      

      setIsSubmitted(true);
      (window as any).gtag?.('event', 'form_submit', {
        event_category: 'engagement',
        event_label: 'course_registration',
      });
      (window as any).fbq?.('track', 'Lead', {
        content_name: course?.title || 'Course form',
        content_category: 'Course',
      });
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }, 5000);
    } catch (err: any) {
      if (err.text) {
        setError(`Грешка при изпращане на имейл: ${err.text}`);
      } else if (err.message) {
        setError(`Грешка: ${err.message}`);
      } else {
        setError('Неуспешно изпращане на формата. Моля, опитайте отново.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Курсът не е намерен</h2>
          <p className="text-gray-600 mb-6">Моля, изберете курс от страницата с курсове.</p>
          <Link to="/courses" className="btn-primary inline-block">
            Виж всички курсове
          </Link>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl mx-auto px-4 text-center"
        >
          <div className="bg-white rounded-2xl p-12 shadow-xl">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-600" />
            </motion.div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Заявката ви е изпратена успешно!
            </h2>
            <p className="text-gray-600 mb-8">
              Благодарим ви за интереса! Ще се свържем с вас скоро, за да потвърдим записването за курса.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/courses')}
                className="btn-primary"
              >
                Виж други курсове
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    message: ''
                  });
                }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Изпрати друга заявка
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 pt-32 md:pt-40 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-6"
        >
          <Link
            to={getBackUrl()}
            className="inline-flex items-center text-gray-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Назад към курсовете
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Course Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-8">
              <div className="mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3">
                  {(course as any).seoH1 || course.title}
                </h1>
                {!(course as any).fullDescription && (
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {course.description}
                  </p>
                )}
                
                {course.status && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="flex items-start">
                      <CheckCircle className={`w-5 h-5 mr-2 mt-0.5 flex-shrink-0 ${
                        course.status === 'Записвания отворени' 
                          ? 'text-green-600' 
                          : 'text-blue-600'
                      }`} />
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Статус</p>
                        <p className={`text-base font-semibold ${
                          course.status === 'Записвания отворени' 
                            ? 'text-green-600' 
                            : 'text-blue-600'
                        }`}>
                          {course.status}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-1">Продължителност</p>
                    <p className="text-base font-semibold text-gray-900">{course.duration}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <div className="mb-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {course.formatted_price}
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  За целия курс
                </p>
              </div>

              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Какво ще научите:
                </h2>
                {(course as any).learningSections ? (
                  <div className="space-y-4">
                    {(course as any).learningSections.map((section: any, sectionIndex: number) => (
                      <div key={sectionIndex}>
                        <h3 className="text-base font-semibold text-gray-800 mb-2">
                          {section.title}
                        </h3>
                        <ul className="space-y-2">
                          {section.items.map((item: string, itemIndex: number) => (
                            <li key={itemIndex} className="flex items-start">
                              <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {course.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.div>

          {/* Registration Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-2 space-y-8"
          >
            {/* Enhanced Course Content */}
            {(course as any).fullDescription && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed text-base mb-4">
                    {(course as any).fullDescription}
                  </p>
                  {(course as any).additionalContent && (
                    <p className="text-gray-700 leading-relaxed text-base">
                      {(course as any).additionalContent}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Оставете контакт за бъдещи записвания
              </h2>
              <p className="text-gray-600 mb-8">
                Попълнете формата по-долу и ще се свържем с вас за потвърждение.
              </p>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Име *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    placeholder="Вашето име"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Имейл адрес *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleEmailBlur}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        emailError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="your@email.com"
                    />
                    {emailError && (
                      <p className="mt-1 text-sm text-red-600">{emailError}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Телефонен номер *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handlePhoneBlur}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        phoneError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+359 888 123 456"
                    />
                    {phoneError && (
                      <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Допълнителна информация
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                    placeholder="Имате ли въпроси или специални изисквания?"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Важно:</strong> След изпращане на заявката, ще се свържем с вас в рамките на 24 часа за потвърждение и детайли относно записването.
                  </p>
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                  className="w-full py-4 px-6 bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Изпращане...
                    </>
                  ) : (
                    'Изпрати заявка за бъдещи записвания'
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CourseRegistration;

